import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';

export type AppLocale = 'hr-HR' | 'en-US' | 'de-DE';

const translations: Record<AppLocale, Record<string, string>> = {
  'hr-HR': {
    'nav.dashboard': 'Početna',
    'nav.tasks': 'Zadaci',
    'nav.workOrders': 'Radni nalozi',
    'nav.routes': 'Vožnje i rute',
    'nav.shifts': 'Smjene',
    'nav.attendance': 'Evidencija rada',
    'nav.workTime': 'Mjesečna evidencija',
    'nav.fieldWork': 'Terenski rad',
    'nav.leave': 'Odsustva',
    'nav.documents': 'Dokumenti',
    'nav.employees': 'Zaposlenici',
    'nav.partners': 'Partneri',
    'nav.settings': 'Postavke',
    'nav.notifications': 'Obavijesti',
    'nav.more': 'Više',
    'action.logout': 'Odjava',
    'action.save': 'Spremi',
    'action.cancel': 'Odustani',
    'action.close': 'Zatvori',
    'action.create': 'Dodaj',
    'action.download': 'Preuzmi',
    'action.approve': 'Odobri',
    'action.reject': 'Odbij',
    'action.search': 'Pretraži',
    'action.refresh': 'Osvježi',
    'action.newTask': 'Novi zadatak',
    'action.newEmployee': 'Novi zaposlenik',
    'search.placeholder': 'Pretraži zadatke, ljude, dokumente…',
    'loading.app': 'Pripremam vaš radni dan…',
    'dashboard.greeting.morning': 'Dobro jutro',
    'dashboard.greeting.day': 'Dobar dan',
    'dashboard.greeting.evening': 'Dobra večer',
    'dashboard.subtitle': 'Evo najvažnijeg za danas. Sve ostalo može pričekati.',
    'dashboard.present': 'Trenutačno na poslu',
    'dashboard.myTasks': 'Moji aktivni zadaci',
    'dashboard.overdue': 'Zadaci u kašnjenju',
    'dashboard.pendingLeave': 'Čeka odobrenje',
    'dashboard.focus': 'Fokus za današnji dan',
    'dashboard.whoWorks': 'Tko radi',
    'dashboard.livePresence': 'Prisutnost u stvarnom vremenu',
    'dashboard.emptyTasks': 'Sve je čisto.',
    'dashboard.emptyTasksBody': 'Trenutačno nema otvorenih zadataka.',
    'dashboard.emptyPresence': 'Nitko još nije evidentirao dolazak.',
    'dashboard.all': 'Prikaži sve',
    'employees.title': 'Zaposlenici i timovi',
    'employees.subtitle': 'Ljudi, uloge, pristup i QR iskaznice na jednom mjestu.',
    'employees.empty': 'Nema zaposlenika koji odgovaraju pretrazi.',
    'employees.invited': 'Pozvan',
    'employees.active': 'Aktivan',
    'employees.locked': 'Zaključan',
    'employees.disabled': 'Onemogućen',
    'employees.limit': 'Iskorišteno korisnika',
    'employees.team': 'Tim',
    'employees.role': 'Uloga',
    'employees.qr': 'QR iskaznica',
    'employees.activation': 'Aktivacijski link',
    'tasks.title': 'Zadaci',
    'tasks.subtitle': 'Dodijeli posao, prati rok i zatvori krug bez poruka sa strane.',
    'tasks.empty': 'Nema zadataka u ovom prikazu.',
    'tasks.assignees': 'Izvršitelji',
    'tasks.due': 'Rok',
    'tasks.priority': 'Prioritet',
    'tasks.status': 'Status',
    'leave.title': 'Odsustva',
    'leave.subtitle': 'Zahtjevi, odobravanja i pregled zauzetosti tima.',
    'leave.new': 'Novi zahtjev',
    'leave.empty': 'Još nema zahtjeva za odsustvo.',
    'documents.title': 'Dokumenti',
    'documents.subtitle': 'Privatna dokumentacija, rokovi i potvrde čitanja.',
    'documents.new': 'Dodaj dokument',
    'documents.empty': 'Još nema dostupnih dokumenata.',
    'partners.title': 'Poslovni partneri',
    'partners.subtitle': 'Klijenti, kontakti, lokacije i povezani posao na jednom mjestu.',
    'workOrders.title': 'Radni nalozi',
    'workOrders.subtitle': 'Od partnera i lokacije do izvršitelja, termina i završnog odobrenja.',
    'shifts.title': 'Šihterica i smjene',
    'shifts.subtitle': 'Tjedni raspored, predlošci, kopiranje i izvoz bez ručnog prepisivanja.',
    'attendance.title': 'Evidencija rada',
    'attendance.subtitle': 'Dolazak, odlazak, QR iskaznice, sati i kontrolirane korekcije.',
    'notifications.title': 'Obavijesti',
    'notifications.subtitle': 'Sve važne promjene, odluke i dodijeljeni poslovi.',
    'notifications.empty': 'Nema novih obavijesti.',
    'notifications.readAll': 'Označi sve pročitanim',
    'settings.title': 'Moje postavke',
    'settings.subtitle': 'Jezik, e-mail i PWA obavijesti prate vaš korisnički račun.',
    'settings.language': 'Jezik aplikacije',
    'settings.push': 'PWA push obavijesti',
    'settings.email': 'E-mail obavijesti',
    'settings.inApp': 'Obavijesti u aplikaciji',
    'settings.enablePush': 'Uključi push na ovom uređaju',
    'common.today': 'Danas',
    'common.live': 'UŽIVO',
    'common.noDeadline': 'bez roka',
    'common.all': 'Sve'
    ,'auth.tagline': 'Tvoj radni dan, pod kontrolom'
    ,'auth.hero': 'Manje kaosa. Više napravljenog.'
    ,'auth.heroBody': 'Zadaci, evidencija, smjene i cijeli tim — sve na jednom mirnom, preglednom mjestu.'
    ,'auth.ready': 'Sustav je spreman'
    ,'auth.welcome': 'Dobro došli natrag'
    ,'auth.login': 'Prijava u aplikaciju'
    ,'auth.continue': 'Nastavite tamo gdje ste stali.'
    ,'auth.identity': 'E-mail ili korisničko ime'
    ,'auth.password': 'Lozinka'
    ,'auth.submit': 'Prijavi se'
    ,'auth.forgot': 'Zaboravili ste lozinku?'
  },
  'en-US': {
    'nav.dashboard': 'Home',
    'nav.tasks': 'Tasks',
    'nav.workOrders': 'Work orders',
    'nav.routes': 'Routes & deliveries',
    'nav.shifts': 'Shifts',
    'nav.attendance': 'Attendance',
    'nav.fieldWork': 'Field work',
    'nav.workTime': 'Monthly records',
    'nav.leave': 'Leave',
    'nav.documents': 'Documents',
    'nav.employees': 'Employees',
    'nav.partners': 'Partners',
    'nav.settings': 'Settings',
    'nav.notifications': 'Notifications',
    'nav.more': 'More',
    'action.logout': 'Log out',
    'action.save': 'Save',
    'action.cancel': 'Cancel',
    'action.close': 'Close',
    'action.create': 'Add',
    'action.download': 'Download',
    'action.approve': 'Approve',
    'action.reject': 'Reject',
    'action.search': 'Search',
    'action.refresh': 'Refresh',
    'action.newTask': 'New task',
    'action.newEmployee': 'New employee',
    'search.placeholder': 'Search tasks, people, documents…',
    'loading.app': 'Preparing your workday…',
    'dashboard.greeting.morning': 'Good morning',
    'dashboard.greeting.day': 'Good afternoon',
    'dashboard.greeting.evening': 'Good evening',
    'dashboard.subtitle': 'Here is what matters today. Everything else can wait.',
    'dashboard.present': 'Currently at work',
    'dashboard.myTasks': 'My active tasks',
    'dashboard.overdue': 'Overdue tasks',
    'dashboard.pendingLeave': 'Awaiting approval',
    'dashboard.focus': 'Today’s focus',
    'dashboard.whoWorks': 'Who is working',
    'dashboard.livePresence': 'Live attendance',
    'dashboard.emptyTasks': 'All clear.',
    'dashboard.emptyTasksBody': 'There are no open tasks right now.',
    'dashboard.emptyPresence': 'No one has clocked in yet.',
    'dashboard.all': 'View all',
    'employees.title': 'Employees and teams',
    'employees.subtitle': 'People, roles, access and QR cards in one place.',
    'employees.empty': 'No employees match your search.',
    'employees.invited': 'Invited',
    'employees.active': 'Active',
    'employees.locked': 'Locked',
    'employees.disabled': 'Disabled',
    'employees.limit': 'User seats used',
    'employees.team': 'Team',
    'employees.role': 'Role',
    'employees.qr': 'QR card',
    'employees.activation': 'Activation link',
    'tasks.title': 'Tasks',
    'tasks.subtitle': 'Assign work, track deadlines and close the loop.',
    'tasks.empty': 'No tasks in this view.',
    'tasks.assignees': 'Assignees',
    'tasks.due': 'Due',
    'tasks.priority': 'Priority',
    'tasks.status': 'Status',
    'leave.title': 'Leave',
    'leave.subtitle': 'Requests, approvals and team availability.',
    'leave.new': 'New request',
    'leave.empty': 'There are no leave requests yet.',
    'documents.title': 'Documents',
    'documents.subtitle': 'Private files, expiry dates and read receipts.',
    'documents.new': 'Add document',
    'documents.empty': 'There are no available documents yet.',
    'partners.title': 'Business partners',
    'partners.subtitle': 'Clients, contacts, locations and related work in one place.',
    'workOrders.title': 'Work orders',
    'workOrders.subtitle': 'From partner and location to assignees, schedule and final approval.',
    'shifts.title': 'Shift schedule',
    'shifts.subtitle': 'Weekly planning, templates, copying and exports without retyping.',
    'attendance.title': 'Attendance',
    'attendance.subtitle': 'Clock-in, clock-out, QR cards, hours and controlled corrections.',
    'notifications.title': 'Notifications',
    'notifications.subtitle': 'Important changes, decisions and assigned work.',
    'notifications.empty': 'No new notifications.',
    'notifications.readAll': 'Mark all as read',
    'settings.title': 'My settings',
    'settings.subtitle': 'Language, email and PWA notifications follow your account.',
    'settings.language': 'Application language',
    'settings.push': 'PWA push notifications',
    'settings.email': 'Email notifications',
    'settings.inApp': 'In-app notifications',
    'settings.enablePush': 'Enable push on this device',
    'common.today': 'Today',
    'common.live': 'LIVE',
    'common.noDeadline': 'no deadline',
    'common.all': 'All'
    ,'auth.tagline': 'Your workday, under control'
    ,'auth.hero': 'Less chaos. More done.'
    ,'auth.heroBody': 'Tasks, attendance, shifts and your whole team — in one calm, clear place.'
    ,'auth.ready': 'System ready'
    ,'auth.welcome': 'Welcome back'
    ,'auth.login': 'Sign in'
    ,'auth.continue': 'Continue where you left off.'
    ,'auth.identity': 'Email or username'
    ,'auth.password': 'Password'
    ,'auth.submit': 'Sign in'
    ,'auth.forgot': 'Forgot your password?'
  },
  'de-DE': {
    'nav.dashboard': 'Startseite',
    'nav.tasks': 'Aufgaben',
    'nav.workOrders': 'Arbeitsaufträge',
    'nav.routes': 'Touren & Lieferungen',
    'nav.shifts': 'Schichten',
    'nav.attendance': 'Zeiterfassung',
    'nav.fieldWork': 'Außendienst',
    'nav.workTime': 'Monatsübersicht',
    'nav.leave': 'Abwesenheiten',
    'nav.documents': 'Dokumente',
    'nav.employees': 'Mitarbeiter',
    'nav.partners': 'Partner',
    'nav.settings': 'Einstellungen',
    'nav.notifications': 'Benachrichtigungen',
    'nav.more': 'Mehr',
    'action.logout': 'Abmelden',
    'action.save': 'Speichern',
    'action.cancel': 'Abbrechen',
    'action.close': 'Schließen',
    'action.create': 'Hinzufügen',
    'action.download': 'Herunterladen',
    'action.approve': 'Genehmigen',
    'action.reject': 'Ablehnen',
    'action.search': 'Suchen',
    'action.refresh': 'Aktualisieren',
    'action.newTask': 'Neue Aufgabe',
    'action.newEmployee': 'Neuer Mitarbeiter',
    'search.placeholder': 'Aufgaben, Personen, Dokumente suchen…',
    'loading.app': 'Ihr Arbeitstag wird vorbereitet…',
    'dashboard.greeting.morning': 'Guten Morgen',
    'dashboard.greeting.day': 'Guten Tag',
    'dashboard.greeting.evening': 'Guten Abend',
    'dashboard.subtitle': 'Das Wichtigste für heute. Alles andere kann warten.',
    'dashboard.present': 'Derzeit bei der Arbeit',
    'dashboard.myTasks': 'Meine aktiven Aufgaben',
    'dashboard.overdue': 'Überfällige Aufgaben',
    'dashboard.pendingLeave': 'Wartet auf Genehmigung',
    'dashboard.focus': 'Fokus für heute',
    'dashboard.whoWorks': 'Wer arbeitet',
    'dashboard.livePresence': 'Anwesenheit in Echtzeit',
    'dashboard.emptyTasks': 'Alles erledigt.',
    'dashboard.emptyTasksBody': 'Derzeit gibt es keine offenen Aufgaben.',
    'dashboard.emptyPresence': 'Noch niemand hat sich angemeldet.',
    'dashboard.all': 'Alle anzeigen',
    'employees.title': 'Mitarbeiter und Teams',
    'employees.subtitle': 'Personen, Rollen, Zugänge und QR-Ausweise an einem Ort.',
    'employees.empty': 'Keine passenden Mitarbeiter gefunden.',
    'employees.invited': 'Eingeladen',
    'employees.active': 'Aktiv',
    'employees.locked': 'Gesperrt',
    'employees.disabled': 'Deaktiviert',
    'employees.limit': 'Verwendete Benutzerplätze',
    'employees.team': 'Team',
    'employees.role': 'Rolle',
    'employees.qr': 'QR-Ausweis',
    'employees.activation': 'Aktivierungslink',
    'tasks.title': 'Aufgaben',
    'tasks.subtitle': 'Arbeit zuweisen, Termine verfolgen und sauber abschließen.',
    'tasks.empty': 'Keine Aufgaben in dieser Ansicht.',
    'tasks.assignees': 'Zugewiesen an',
    'tasks.due': 'Fällig',
    'tasks.priority': 'Priorität',
    'tasks.status': 'Status',
    'leave.title': 'Abwesenheiten',
    'leave.subtitle': 'Anträge, Genehmigungen und Teamverfügbarkeit.',
    'leave.new': 'Neuer Antrag',
    'leave.empty': 'Noch keine Abwesenheitsanträge.',
    'documents.title': 'Dokumente',
    'documents.subtitle': 'Private Dateien, Fristen und Lesebestätigungen.',
    'documents.new': 'Dokument hinzufügen',
    'documents.empty': 'Noch keine Dokumente verfügbar.',
    'partners.title': 'Geschäftspartner',
    'partners.subtitle': 'Kunden, Kontakte, Standorte und zugehörige Arbeit an einem Ort.',
    'workOrders.title': 'Arbeitsaufträge',
    'workOrders.subtitle': 'Vom Partner und Standort bis zu Mitarbeitern, Termin und Freigabe.',
    'shifts.title': 'Schichtplan',
    'shifts.subtitle': 'Wochenplanung, Vorlagen, Kopieren und Export ohne Abschreiben.',
    'attendance.title': 'Zeiterfassung',
    'attendance.subtitle': 'Kommen, Gehen, QR-Ausweise, Stunden und kontrollierte Korrekturen.',
    'notifications.title': 'Benachrichtigungen',
    'notifications.subtitle': 'Wichtige Änderungen, Entscheidungen und Aufgaben.',
    'notifications.empty': 'Keine neuen Benachrichtigungen.',
    'notifications.readAll': 'Alle als gelesen markieren',
    'settings.title': 'Meine Einstellungen',
    'settings.subtitle': 'Sprache, E-Mail und PWA-Benachrichtigungen gehören zu Ihrem Konto.',
    'settings.language': 'Anwendungssprache',
    'settings.push': 'PWA-Push-Benachrichtigungen',
    'settings.email': 'E-Mail-Benachrichtigungen',
    'settings.inApp': 'In-App-Benachrichtigungen',
    'settings.enablePush': 'Push auf diesem Gerät aktivieren',
    'common.today': 'Heute',
    'common.live': 'LIVE',
    'common.noDeadline': 'ohne Termin',
    'common.all': 'Alle'
    ,'auth.tagline': 'Ihr Arbeitstag unter Kontrolle'
    ,'auth.hero': 'Weniger Chaos. Mehr erledigt.'
    ,'auth.heroBody': 'Aufgaben, Zeiterfassung, Schichten und das ganze Team — an einem übersichtlichen Ort.'
    ,'auth.ready': 'System ist bereit'
    ,'auth.welcome': 'Willkommen zurück'
    ,'auth.login': 'Anmelden'
    ,'auth.continue': 'Machen Sie dort weiter, wo Sie aufgehört haben.'
    ,'auth.identity': 'E-Mail oder Benutzername'
    ,'auth.password': 'Passwort'
    ,'auth.submit': 'Anmelden'
    ,'auth.forgot': 'Passwort vergessen?'
  }
};

interface LanguageContextValue {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
  t: (key: string, variables?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

function storedLocale(): AppLocale {
  const stored = localStorage.getItem('sihterica_locale');
  if (stored === 'en-US' || stored === 'de-DE' || stored === 'hr-HR') return stored;
  const browser = navigator.language.toLowerCase();
  if (browser.startsWith('de')) return 'de-DE';
  if (browser.startsWith('en')) return 'en-US';
  return 'hr-HR';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<AppLocale>(storedLocale);

  useEffect(() => {
    document.documentElement.lang = locale.slice(0, 2);
    localStorage.setItem('sihterica_locale', locale);
  }, [locale]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      locale,
      setLocale: setLocaleState,
      t: (key, variables) => {
        let value = translations[locale][key] ?? translations['hr-HR'][key] ?? key;
        for (const [variable, replacement] of Object.entries(variables ?? {})) {
          value = value.replaceAll(`{${variable}}`, String(replacement));
        }
        return value;
      }
    }),
    [locale]
  );
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useTranslation(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('LanguageProvider nedostaje.');
  return context;
}
